// Cashfree Integration Utility for AstroShubh — pure custom/headless checkout,
// mirrors razorpayUtils.js's role but for Cashfree's Order Pay API.
//
// Unlike Razorpay, this needs no SDK script: Cashfree's Order Pay endpoint
// (POST {cashfreeBaseUrl}/orders/sessions) is session-scoped (payment_session_id)
// and explicitly documented as callable directly from the browser with no
// client credentials — so this file is a plain fetch wrapper, not a loader.

import { publicApi } from '../api/api';

// Card payments on Cashfree are hidden from the checkout (2026-10-17): the card
// flow below submits raw card details straight to Cashfree's Order Pay API, which
// Cashfree rejects on our production account with "server to server card
// submission is not allowed" until it is whitelisted (or card entry is moved to
// Cashfree's hosted Elements fields). Set to true to bring the Card option back.
// Razorpay's card option is unaffected.
export const CASHFREE_CARD_ENABLED = false;

/**
 * Fetches the LIVE netbanking banks + wallet providers actually enabled for
 * this merchant account (backend: GET /public/cashfree-payment-methods,
 * itself calling Cashfree's Get Eligible Payment Methods API) — added
 * 2026-09-17 to replace CASHFREE_POPULAR_BANKS/CASHFREE_POPULAR_WALLETS
 * below as the source of truth. Those were a hand-curated guess at what's
 * enabled (found live on STG to have drifted from reality — a listed bank
 * wasn't actually eligible, rejected by Cashfree) since Cashfree has no
 * dynamic "list enabled banks" endpoint... except this eligibility one.
 *
 * Called independently by both CheckoutContext.jsx (checkout flow) and
 * WalletRecharge.jsx (which isn't wrapped in CheckoutProvider) — kept here
 * as a single shared fetch rather than duplicated in both.
 *
 * Never throws — callers should treat the empty-array fallback as "use the
 * static list instead", not as an error state to surface to the customer.
 *
 * @returns {Promise<{netbanking: Array<{code:number, nick:string, display:string}>, wallet: Array<{code, nick, display}>}>}
 */
export async function fetchCashfreeEligiblePaymentMethods(amount) {
  try {
    // amount (rupees, optional): eligibility can depend on the order amount, so
    // callers that know it pass it; omitted -> backend uses its nominal default.
    const params = Number(amount) > 0 ? { amount: Number(amount) } : undefined;
    const { data } = await publicApi.get('/cashfree-payment-methods', { params });
    return {
      netbanking: Array.isArray(data?.netbanking) ? data.netbanking : [],
      wallet: Array.isArray(data?.wallet) ? data.wallet : [],
    };
  } catch (_err) {
    return { netbanking: [], wallet: [] };
  }
}

// Curated Cashfree-only netbanking bank list (2026-09-13 — Cashfree actually
// supports 97 banks per its docs, cashfree.com/docs/payments/manage/
// payment-methods/netbanking; NetbankingSection.jsx's original 10-bank
// POPULAR_BANKS list was scoped to match Razorpay's own picker, not
// Cashfree's real coverage — a real gap found live on STG). `code` here is
// Cashfree's own netbanking_bank_code directly (not Razorpay's alpha codes),
// so buildCashfreePaymentMethod below needs no separate lookup table — same
// {code, name, color} shape NetbankingSection.jsx's own POPULAR_BANKS already
// uses, so the component can render either list unchanged.
//
// Deliberately curated (~30 well-known banks), not all 97 — a flat list/
// "view all" toggle UI doesn't scale to 97 entries; would need a searchable
// dropdown for full coverage, out of scope for this pass.
export const CASHFREE_POPULAR_BANKS = [
  { code: '3044', name: 'State Bank of India',      color: '#1565c0' },
  { code: '3021', name: 'HDFC Bank',                 color: '#004c97' },
  { code: '3022', name: 'ICICI Bank',                color: '#b71c1c' },
  { code: '3003', name: 'Axis Bank',                 color: '#97144d' },
  { code: '3032', name: 'Kotak Mahindra Bank',       color: '#ed1c24' },
  { code: '3038', name: 'Punjab National Bank',      color: '#ff6400' },
  { code: '3009', name: 'Canara Bank',               color: '#004f38' },
  { code: '3028', name: 'IndusInd Bank',             color: '#00478e' },
  { code: '3058', name: 'Yes Bank',                  color: '#003479' },
  { code: '3024', name: 'IDFC First Bank',           color: '#8b1a1a' },
  { code: '3005', name: 'Bank of Baroda',            color: '#f36f21' },
  { code: '3006', name: 'Bank of India',             color: '#a71930' },
  { code: '3055', name: 'Union Bank of India',       color: '#003b71' },
  { code: '3011', name: 'Central Bank of India',     color: '#5e2129' },
  { code: '3026', name: 'Indian Bank',               color: '#7b0828' },
  { code: '3027', name: 'Indian Overseas Bank',      color: '#00447c' },
  { code: '3054', name: 'UCO Bank',                  color: '#004c8f' },
  { code: '3007', name: 'Bank of Maharashtra',       color: '#7a1416' },
  { code: '3037', name: 'Punjab & Sind Bank',        color: '#8e1537' },
  { code: '3020', name: 'Federal Bank',              color: '#a4272c' },
  { code: '3042', name: 'South Indian Bank',         color: '#00693e' },
  { code: '3030', name: 'Karnataka Bank',            color: '#004a93' },
  { code: '3031', name: 'Karur Vysya Bank',          color: '#7c1f2d' },
  { code: '3039', name: 'RBL Bank',                  color: '#e2231a' },
  { code: '3012', name: 'City Union Bank',           color: '#004f9f' },
  { code: '3023', name: 'IDBI Bank',                 color: '#004990' },
  { code: '3043', name: 'Standard Chartered Bank',   color: '#0072ce' },
  { code: '3017', name: 'DBS Bank',                  color: '#e30613' },
  { code: '3087', name: 'AU Small Finance Bank',     color: '#e2231a' },
  { code: '3029', name: 'Jammu and Kashmir Bank',    color: '#8b1a1a' },
];

// Curated Cashfree-only wallet list — WalletSection.jsx's original 5-wallet
// list matches Razorpay's picker; Cashfree's real provider enum (per its
// Order Pay API docs) has 9: gpay, phonepe, ola, paytm, amazon, airtel,
// freecharge, mobikwik, jio. `id` here is Cashfree's own provider value
// directly, so buildCashfreePaymentMethod needs no separate id-mapping for
// these entries. gpay omitted — already covered by the UPI intent flow, and
// listing it again as a "wallet" here would be a confusing duplicate.
export const CASHFREE_POPULAR_WALLETS = [
  { id: 'phonepe',    name: 'PhonePe',      color: '#5f259f', initial: 'P', logo: null },
  { id: 'amazon',     name: 'Amazon Pay',   color: '#ff9900', initial: 'A', logo: null },
  { id: 'paytm',      name: 'Paytm',        color: '#00baf2', initial: 'P', logo: null },
  { id: 'mobikwik',   name: 'MobiKwik',     color: '#00bcd4', initial: 'M', logo: null },
  { id: 'freecharge', name: 'FreeCharge',   color: '#e65100', initial: 'F', logo: null },
  { id: 'ola',        name: 'Ola Money',    color: '#98d13d', initial: 'O', logo: null },
  { id: 'airtel',     name: 'Airtel Money', color: '#e40000', initial: 'A', logo: null },
  { id: 'jio',        name: 'JioMoney',     color: '#0f1b5f', initial: 'J', logo: null },
];

/**
 * Pulls the deep link to open for a tapped UPI app out of a `upi` +
 * `channel:'link'` Order Pay response. Cashfree returns per-app links under
 * `data.payload` (gpay / phonepe / paytm / bhim / default); falls back to
 * `payload.default`, then `data.url`. Returns null if nothing usable.
 */
export function pickCashfreeUpiIntentUrl(result, app) {
  const payload = result?.data?.payload;
  const fromPayload = payload && typeof payload === 'object'
    ? (payload[app] || payload.default)
    : null;
  return fromPayload || result?.data?.url || null;
}

/**
 * Submits payment details directly to Cashfree's Order Pay API from the
 * browser. Response shape varies by `action`:
 *   - "link"   -> { data: { url } }            — browser redirect required
 *   - "post"   -> { data: { url, method, ... } } — native-OTP custom flow
 *   - "custom" -> { data: { vpa, expiry } }     — e.g. UPI collect, no redirect
 *
 * @param {string} cashfreeBaseUrl - e.g. https://sandbox.cashfree.com/pg (from
 *   the normalized order object services/paymentGatewayService.js::createOrder returns)
 * @param {string} paymentSessionId
 * @param {object} paymentMethod - Cashfree's payment_method object, see
 *   buildCashfreePaymentMethod() below
 * @returns {Promise<object>} the raw Cashfree Order Pay response
 */
export async function submitCashfreePayment(cashfreeBaseUrl, paymentSessionId, paymentMethod) {
  const res = await fetch(`${cashfreeBaseUrl}/orders/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payment_session_id: paymentSessionId, payment_method: paymentMethod }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.message || data?.error_description || data?.error?.message || 'Payment failed. Please try again.';
    throw new Error(message);
  }
  return data;
}

// WalletSection.jsx's internal ids, PLUS UpiIntentSection.jsx's per-app UPI
// tiles (gpay/phonepe/paytm) -> Cashfree's exact `provider` enum (gpay,
// phonepe, ola, paytm, amazon, airtel, freecharge, mobikwik, jio — confirmed
// via Cashfree's Order Pay API docs). Bug found live on STG (2026-09-13)
// alongside the missing `phone` field below: our id 'amazonpay' doesn't
// match Cashfree's 'amazon' at all — would have failed with an
// invalid-provider error the first time anyone tried it. Ids not listed
// here pass through unchanged rather than silently becoming undefined.
//
// gpay added 2026-09-13: Cashfree's own docs explicitly say
// payment_method.upi (channel:'link', UPI intent) has NO per-app targeting
// at all — always the same generic upi://pay link regardless of which app
// tile the user tapped, real app that answers is down to the OS/whichever
// app claims the scheme first. Their docs directly say: "for targeting
// specific providers, you would use the separate `app` payment method with
// named providers (gpay, phonepe, ola, etc.), not the UPI intent flow." So
// GPay/PhonePe/Paytm "UPI app" tiles now route through this SAME `app`
// mechanism (see buildCashfreePaymentMethod's 'upi-intent' branch below)
// instead of the generic upi channel, to genuinely target that specific app.
//
// Keys cover BOTH id spaces that can reach this map: CASHFREE_POPULAR_WALLETS'
// static-fallback ids (amazonpay, ola, jio, airtel, ...) AND the live Cashfree
// eligibility API's `nick` values (confirmed via a real sandbox response,
// 2026-09-17: GET {baseUrl}/eligibility/payment_methods, entity_value:'wallet').
// The two DON'T match 1:1 — eligibility nicks are 'ola_money', 'reliance_jio_money',
// 'airtel_money', 'amazon_pay' vs the provider enum's 'ola'/'jio'/'airtel'/'amazon'.
// Sending the nick straight through as `provider` (the old `|| details.wallet`
// fallback) would have failed with an invalid-provider error the first time a
// customer picked Ola Money, JioMoney, Airtel Money, or Amazon Pay via the live
// list — freecharge/mobikwik/phonepe happened to match by coincidence, masking
// the bug. `test_wallet` (sandbox-only) and `cashfree_pay` are deliberately NOT
// mapped here — neither is part of Cashfree's documented app.provider enum, so
// WalletSection.jsx filters live entries down to only known providers.
export const CASHFREE_APP_PROVIDERS = {
  gpay: 'gpay',
  phonepe: 'phonepe',
  amazonpay: 'amazon',
  amazon_pay: 'amazon',
  paytm: 'paytm',
  mobikwik: 'mobikwik',
  freecharge: 'freecharge',
  ola: 'ola',
  ola_money: 'ola',
  jio: 'jio',
  reliance_jio_money: 'jio',
  airtel: 'airtel',
  airtel_money: 'airtel',
};

/**
 * Maps the same { method, app, details } shape CardSection / NetbankingSection /
 * WalletSection / UpiCollectSection / UpiIntentSection already call `onPay`
 * with (i.e. CheckoutPage.jsx's existing handlePay contract) into Cashfree's
 * payment_method object. Nothing about the calling components needs to change.
 *
 * @param {string} [customerPhone] - required for method:'wallet' (Cashfree's
 *   payment_method.app.phone is a mandatory field per its Order Pay API docs —
 *   confirmed live on STG, 2026-09-13, via a real "phone: is missing in the
 *   request" error). Every other method ignores this param.
 */
export function buildCashfreePaymentMethod({ method, app, details = {}, customerPhone }) {
  if (method === 'card') {
    const cd = details.cardDetails || {};
    return {
      card: {
        channel: 'link',
        card_number: cd.number,
        card_holder_name: cd.name,
        card_expiry_mm: cd.expiryMonth,
        card_expiry_yy: String(cd.expiryYear).slice(-2),
        card_cvv: cd.cvv,
      },
    };
  }
  if (method === 'netbanking') {
    // details.bank is Cashfree's own netbanking_bank_code — CASHFREE_POPULAR_BANKS
    // above carries it directly, no separate alpha->numeric lookup needed
    // (unlike the old 10-bank list, which reused Razorpay's alpha codes and
    // needed CASHFREE_BANK_CODES to translate them).
    if (!details.bank) {
      throw new Error('This bank is not yet supported for Cashfree netbanking. Please choose another bank.');
    }
    // Bug fix (found live on STG, 2026-09-17): Cashfree's own docs specify
    // netbanking_bank_code as type:integer (format int32) — CASHFREE_POPULAR_BANKS'
    // `code` values are strings ('3044'), so this was sending a quoted string
    // where Cashfree requires a bare number, rejected with "invalid value
    // for parameter: netbanking_bank_code" on every single bank.
    return { netbanking: { channel: 'link', netbanking_bank_code: Number(details.bank) } };
  }
  if (method === 'wallet') {
    if (!customerPhone) {
      throw new Error('Missing customer phone number for wallet payment.');
    }
    return {
      app: {
        channel: 'link',
        provider: CASHFREE_APP_PROVIDERS[details.wallet] || details.wallet,
        phone: customerPhone,
      },
    };
  }
  if (method === 'upi') {
    // UpiCollectSection.jsx's VPA form — same for both gateways.
    return { upi: { channel: 'collect', upi_id: details.vpa } };
  }
  if (method === 'upi-intent') {
    // Real per-app targeting (2026-09-13, see CASHFREE_APP_PROVIDERS'
    // comment above for why) for GPay/PhonePe/Paytm tiles — same mechanism
    // as method:'wallet' above, just entered from UpiIntentSection.jsx
    // instead of WalletSection.jsx. The generic "Other UPI app" tile (app
    // id 'upi_app', no known provider) falls through to the old generic
    // upi channel — no way to target a specific app for it anyway.
    // Updated 2026-10-16: every UPI app tile now uses the real UPI intent
    // (`upi` + channel:'link'); the tapped app's deep link is then picked out
    // of the response's data.payload (see pickCashfreeUpiIntentUrl). The old
    // `app`/provider (wallet-style) route needed GPay/Paytm enabled as wallet
    // providers on the merchant account, which they aren't.
    return { upi: { channel: 'link' } };
  }
  throw new Error(`Unsupported payment method for Cashfree: ${method}`);
}
